import React from "react"
import { withFormik } from "formik"
import { object, string, boolean } from "yup"
import classNames from "classnames"
import styles from "../SignInForm/SignInForm.module.scss"
import mfaStyles from "./MfaVerifyForm.module.scss"
import OtpCodeInput from "../../components/OtpCodeInput/OtpCodeInput"
import Button from "../../components/Button/Button"
import {
  isMfaSessionExpiredError,
  getMfaErrorMessage,
} from "../../helpers/mfaErrors"

const RECOVERY_CODE_LENGTH = 16

const normalizeRecoveryCode = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, RECOVERY_CODE_LENGTH)

const ShieldSmallIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2 4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z"
    />
  </svg>
)

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.75 10h-2.1A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
    />
  </svg>
)

const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"
    />
  </svg>
)

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <path
      fill="currentColor"
      d="M17 1.01 7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"
    />
  </svg>
)

const MfaVerifyForm = ({
  values,
  errors,
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldError,
  setFieldTouched,
  touched,
  isSubmitting,
  onResend,
  onResendSms,
  resendDisabled,
  smsResendDisabled,
  smsSending = false,
  smsBackupAvailable = false,
  otpChannel = "email",
  resendMessage,
  sessionExpired,
  statusMessage,
  onReturnToLogin,
  verificationMode,
  onSwitchMode,
}) => {
  const formDisabled = sessionExpired
  const isRecovery = verificationMode === "recovery"
  const isSmsOtp = otpChannel === "sms"

  const switchMode = (mode) => {
    setFieldValue("otp", "")
    setFieldValue("recovery_code", "")
    setFieldError("otp", undefined)
    setFieldError("recovery_code", undefined)
    setFieldTouched("otp", false)
    setFieldTouched("recovery_code", false)
    onSwitchMode(mode)
  }

  const canSubmit = isRecovery
    ? normalizeRecoveryCode(values.recovery_code).length === RECOVERY_CODE_LENGTH
    : values.otp.length === 6

  const recoveryError =
    errors.recovery_code && touched.recovery_code && errors.recovery_code

  return (
    <form className={classNames(styles.signInForm, mfaStyles.form)} onSubmit={handleSubmit}>
      <div className={styles.inlineWrap}>
        <h4 className={classNames(styles.title, mfaStyles.title)}>
          {isRecovery
            ? "Verify Recovery Code"
            : isSmsOtp
              ? "Verify Text Code"
              : "Verify Email Code"}
        </h4>
        <p className={mfaStyles.subtitle}>
          {sessionExpired
            ? "This verification session is no longer valid."
            : isRecovery
              ? "Enter one of your 16-digit recovery codes."
              : isSmsOtp
                ? "Enter the 6-digit code we sent to your phone."
                : "Enter the 6-digit code we sent to your email."}
        </p>

        {statusMessage && (
          <p
            className={classNames(
              mfaStyles.statusMessage,
              sessionExpired && mfaStyles.statusError,
            )}
          >
            {statusMessage}
          </p>
        )}

        {isRecovery ? (
          <div className={mfaStyles.otpWrap}>
            <div
              className={classNames(
                mfaStyles.recoveryInputWrap,
                recoveryError && mfaStyles.recoveryInputError,
              )}
            >
              <span className={mfaStyles.recoveryInputIcon} aria-hidden="true">
                <ShieldSmallIcon />
              </span>
              <input
                name="recovery_code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className={mfaStyles.recoveryInput}
                placeholder="Enter 16-digit recovery code"
                value={values.recovery_code}
                onChange={(e) =>
                  setFieldValue(
                    "recovery_code",
                    normalizeRecoveryCode(e.target.value),
                  )
                }
                onBlur={handleBlur}
                disabled={formDisabled}
                autoFocus={!formDisabled}
                aria-label="Recovery code"
              />
            </div>
            <div className={mfaStyles.fieldHint}>16-digit code</div>
            {recoveryError ? (
              <div className={mfaStyles.fieldError}>{recoveryError}</div>
            ) : null}
          </div>
        ) : (
          <div className={mfaStyles.otpWrap}>
            <OtpCodeInput
              name="otp"
              value={values.otp}
              onChange={(e) => setFieldValue("otp", e.target.value)}
              onBlur={handleBlur}
              error={errors.otp && touched.otp}
              errorMessage={errors.otp && touched.otp && errors.otp}
              disabled={formDisabled}
              autoFocus={!formDisabled}
            />
          </div>
        )}

        <div className={mfaStyles.orDivider} aria-hidden="true">
          <span>OR</span>
        </div>

        {isRecovery ? (
          <button
            type="button"
            className={mfaStyles.textLink}
            onClick={() => switchMode("email")}
            disabled={formDisabled}
          >
            <ArrowLeftIcon />
            <span>{isSmsOtp ? "Back to Text Code" : "Back to Email Code"}</span>
          </button>
        ) : (
          <button
            type="button"
            className={mfaStyles.textLink}
            onClick={() => switchMode("recovery")}
            disabled={formDisabled}
          >
            <ShieldSmallIcon />
            <span>
              {isSmsOtp
                ? "Can't access your phone? Use a recovery code"
                : "Can't access your email? Use a recovery code"}
            </span>
          </button>
        )}

        <label
          className={classNames(
            mfaStyles.rememberRow,
            formDisabled && mfaStyles.disabledRow,
          )}
        >
          <input
            type="checkbox"
            name="remember_device"
            checked={values.remember_device}
            onChange={handleChange}
            disabled={formDisabled}
          />
          <span>Remember this device for 30 days</span>
        </label>

        <Button
          className={styles.signInBtn}
          width="100%"
          disabled={formDisabled || isSubmitting || !canSubmit}
          type="submit"
        >
          Verify
        </Button>

        <div
          className={classNames(
            mfaStyles.footerActions,
            isRecovery && mfaStyles.footerActionsSingle,
          )}
        >
          {!isRecovery && (
            <button
              type="button"
              className={mfaStyles.footerBtnSecondary}
              onClick={onResend}
              disabled={resendDisabled || formDisabled}
            >
              <RefreshIcon />
              <span>Resend Code</span>
            </button>
          )}
          <button
            type="button"
            className={mfaStyles.footerBtnSecondary}
            onClick={onReturnToLogin}
          >
            <ArrowLeftIcon />
            <span>Return to Login</span>
          </button>
        </div>

        {!isRecovery && resendMessage && (
          <p className={mfaStyles.resendMessage}>{resendMessage}</p>
        )}

        {!isRecovery && !sessionExpired && (
          <p className={mfaStyles.helpText}>
            {isSmsOtp
              ? "Didn't receive the code? Check your text messages or request another text."
              : `Didn't receive the code? Check your spam folder or resend the code.${
                  smsBackupAvailable
                    ? " You can also request a text to your mobile number."
                    : ""
                }`}
          </p>
        )}

        {!isRecovery && !sessionExpired && smsBackupAvailable && onResendSms && (
          <button
            type="button"
            className={classNames(
              mfaStyles.textLink,
              smsSending && mfaStyles.smsSending,
            )}
            onClick={onResendSms}
            disabled={smsResendDisabled || formDisabled}
            aria-busy={smsSending}
          >
            <PhoneIcon />
            <span>
              {smsSending
                ? "Sending text to your phone..."
                : isSmsOtp
                  ? "Send another text"
                  : "Didn't get the email? Send code by SMS"}
            </span>
          </button>
        )}

        {isRecovery && !sessionExpired && (
          <p className={classNames(mfaStyles.helpText, mfaStyles.helpTextWithIcon)}>
            <span className={mfaStyles.helpIcon} aria-hidden="true">
              <ShieldSmallIcon />
            </span>
            <span>
              Each recovery code can only be used once. After successful
              verification, the code will be invalidated.
            </span>
          </p>
        )}
      </div>
    </form>
  )
}

const FormikApp = withFormik({
  mapPropsToValues() {
    return {
      otp: "",
      recovery_code: "",
      remember_device: false,
    }
  },
  validationSchema: (props) => {
    const isRecovery = props.verificationMode === "recovery"
    if (isRecovery) {
      return object().shape({
        recovery_code: string()
          .matches(/^\d{16}$/, "Please enter a valid recovery code")
          .required("Please enter a recovery code"),
        remember_device: boolean(),
      })
    }
    return object().shape({
      otp: string()
        .matches(/^\d{6}$/, "Please enter a valid 6-digit code")
        .required("Please enter the verification code"),
      remember_device: boolean(),
    })
  },
  // Prevent "click anywhere" blur from immediately showing validation errors.
  // We still validate on submit, so errors appear when the user clicks "Verify".
  validateOnBlur: false,
  validateOnChange: false,
  handleSubmit(
    values,
    {
      setSubmitting,
      setFieldError,
      props: { handleVerify, sessionExpired, verificationMode },
    },
  ) {
    if (sessionExpired) {
      setSubmitting(false)
      return
    }

    const isRecovery = verificationMode === "recovery"
    const payload = isRecovery
      ? {
          recovery_code: normalizeRecoveryCode(values.recovery_code),
          remember_device: values.remember_device,
        }
      : {
          otp: values.otp,
          remember_device: values.remember_device,
        }

    handleVerify(payload)
      .then(() => {
        setSubmitting(false)
      })
      .catch((e) => {
        if (e.response?.status === 429) {
          setSubmitting(false)
          return
        }
        if (isMfaSessionExpiredError(e)) {
          setSubmitting(false)
          return
        }
        const message = getMfaErrorMessage(e)
        setFieldError(isRecovery ? "recovery_code" : "otp", message)
        setSubmitting(false)
      })
  },
})(MfaVerifyForm)

export default FormikApp
